//system
import { NgModule, CUSTOM_ELEMENTS_SCHEMA } from "@angular/core";
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from "@angular/material/core";
import { BrowserModule } from "@angular/platform-browser";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";
import { MatNativeDateModule } from "@angular/material/core";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { MaterialModule } from "../material-module";
import { ToastrModule } from "ngx-toastr";
import { CommonModule } from "@angular/common";
import { RecaptchaModule } from "ng-recaptcha";
import { FlexLayoutModule } from "@angular/flex-layout";
import { LyImageCropperModule } from "@alyle/ui/image-cropper";
import { LyThemeModule, LY_THEME } from "@alyle/ui";
import { MinimaLight } from "@alyle/ui/themes/minima";
import { MomentDateAdapter } from "@angular/material-moment-adapter";
import { MaterialFileInputModule } from "ngx-material-file-input";
import { NgxMaterialTimepickerModule } from "ngx-material-timepicker";
import { LayoutModule } from "@angular/cdk/layout";
import { MatCarouselModule } from "@thouet/material-carousel";

import { MsalModule, MsalRedirectComponent } from "@azure/msal-angular";
import { InteractionType, PublicClientApplication } from "@azure/msal-browser";

import { environment } from "../environments/environment";

//common
import { appRouting } from "./app.routing";
import { AppComponent } from "./app.component";
import { LoaderComponent, LoaderModule } from "./_components/loader/loader.module";
import { JwtInterceptor } from "./_helpers";
import { AppGlobals } from "./app.globals";
import { MenuComponent } from "./menu";
import { ToolbarComponent } from "./toolbar";
import { AppOverlayModule } from "./app.overlay.module";
import { UnSavedChangesDialogComponent } from "./_components/unSavedChangesDialog/unSavedChangesDialog.component";
import { ExcludeUsedFilterPipe } from "./_filters/excludeUsedFilter.pipe";
import { NoDoubleClickDirective } from "./_helpers/forms/noDoubleClick.directive";
import { ConfirmationDialogComponent } from "./_components/confirmationDialog/confirmationDialog.component";
import { FocusFirstInvalidFieldDirective } from "./_helpers/forms/focusFirstInvalidField.directive";
import { SafeUrlPipe } from "./_filters/safeResourceUrl.pipe";

//basic functionality
import { HomeComponent } from "./home";
import { LoginComponent } from "./login";
import { RegisterComponent } from "./register";
import { RegisterDobConfirmationDialogComponent } from "./register/registerDobConfirmation";
import { ForgotPasswordComponent } from "./forgotPassword";
import { ResetPasswordComponent } from "./resetPassword";

//student
import { ProfileDialogComponent } from "./profile";
import { ChangePasswordDialogComponent } from "./changePassword";
import { ProfileImageDialogComponent } from "./profile/profileImage";
import { IdentityDocumentImagesDialogComponent } from "./profile/identityDocumentImages";
import { IdentityDocumentHelpDialogComponent } from "./profile/identityDocumentHelp";
import { ProfileImageHelpDialogComponent } from "./profile/profileImageHelp";

//admissions
import { AdmissionsComponent } from "./admissions";
import { AdmissionsApplicationDialogComponent } from "./admissions/application";
import { AdmissionsApplicationDocumentsDialogComponent } from "./admissions/applicationDocuments";
import { AdmissionsApplicationDocumentUploadDialogComponent } from "./admissions/applicationDocuments/applicationDocumentUpload";

//housing
import { HousingComponent } from "./housing";
import { HousingApplicationDialogComponent } from "./housing/application";
import { RoommatesDialogComponent } from "./housing/roommates";
import { ArrivalDialogComponent } from "./housing/arrival";
import { CheckoutDialogComponent } from "./housing/checkout";

//orientation
import { OrientationComponent } from "./orientation";
import { OrientationPreArrivalFormDialogComponent } from "./orientation/preArrivalForm";
import { OrientationCheckListItemsDialogComponent } from "./orientation/checkListItems";
import { OrientationCheckListItemDocumentUploadDialogComponent } from "./orientation/checkListItems/checkListItemDocumentUpload";
import { OrientationCheckListItemVideoDialogComponent } from "./orientation/checkListItems/checkListItemVideos";

//permit to stay
import { PermitToStayComponent } from "./permitToStay";
import { PermitToStayCheckListItemsDialogComponent } from "./permitToStay/checkListItems";
import { PermitToStayCheckListItemDocumentUploadDialogComponent } from "./permitToStay/checkListItems/checkListItemDocumentUpload";
import { PermitToStayAppointmentsDialogComponent } from "./permitToStay/appointments";

//services
import { StorageService, AlertService, GoogleAnalyticsService, MediaWatcherService  } from "./_services";

export const dateFormats = {
    parse: {
        dateInput: "L",
    },
    display: {
        dateInput: "ll",
        monthYearLabel: "MMM YYYY",
        dateA11yLabel: "ll",
        monthYearA11yLabel: "MMM YYYY",
    },
};

const isIE =
    window.navigator.userAgent.indexOf("MSIE ") > -1 ||
    window.navigator.userAgent.indexOf("Trident/") > -1;

@NgModule(({
    declarations: [
        AppComponent,
        HomeComponent,
        LoginComponent,
        RegisterComponent,
        ForgotPasswordComponent,
        ToolbarComponent,
        MenuComponent,
        ProfileDialogComponent,
        ProfileImageDialogComponent,
        IdentityDocumentImagesDialogComponent,
        ResetPasswordComponent,
        ChangePasswordDialogComponent,
        AdmissionsComponent,
        AdmissionsApplicationDialogComponent,
        UnSavedChangesDialogComponent,
        HousingComponent,
        HousingApplicationDialogComponent,
        ExcludeUsedFilterPipe,
        RoommatesDialogComponent,
        ArrivalDialogComponent,
        CheckoutDialogComponent,
        AdmissionsApplicationDocumentsDialogComponent,
        AdmissionsApplicationDocumentUploadDialogComponent,
        RegisterDobConfirmationDialogComponent,
        NoDoubleClickDirective,
        FocusFirstInvalidFieldDirective,
        ConfirmationDialogComponent,
        OrientationComponent,
        OrientationPreArrivalFormDialogComponent,
        OrientationCheckListItemsDialogComponent,
        OrientationCheckListItemDocumentUploadDialogComponent,
        OrientationCheckListItemVideoDialogComponent,
        SafeUrlPipe,
        PermitToStayComponent,
        PermitToStayCheckListItemsDialogComponent,
        PermitToStayCheckListItemDocumentUploadDialogComponent,
        PermitToStayAppointmentsDialogComponent,
        IdentityDocumentHelpDialogComponent,
        ProfileImageHelpDialogComponent
    ],
    imports: [
        BrowserModule,
        HttpClientModule,
        appRouting,
        CommonModule,
        BrowserAnimationsModule,
        FlexLayoutModule,
        LyImageCropperModule,
        LyThemeModule.setTheme("minima-light"),
        ToastrModule.forRoot({
            timeOut: 1000,
            positionClass: "toast-top-center",
            preventDuplicates: true,
            progressBar: true,
            progressAnimation: "increasing",
            maxOpened: 1,
            closeButton: true,
            iconClasses: {
                error: "toast-error",
                info: "toast-info",
                success: "toast-success",
                warning: "toast-warning"
            }
        }),
        MsalModule.forRoot(
            new PublicClientApplication({
                auth: {
                    clientId: "798ba75a-ba06-41b1-83ab-cb9ce491d040", // Application (client) ID from the app registration
                    authority: "https://login.microsoftonline.com/6da03426-d4c5-4c61-8b5b-0cd19e54ace9", // The Azure cloud instance and the app's sign-in audience (tenant ID, common, organizations, or consumers)
                    redirectUri: `${environment.azureRedirectUri}`, // This is your redirect URI
                },
                cache: {
                    cacheLocation: "localStorage",
                    storeAuthStateInCookie: isIE, // Set to true for Internet Explorer 11
                },
            }),
            {
                interactionType: InteractionType.Redirect, // MSAL Guard Configuration
                authRequest: {
                    scopes: ["api://212fb594-3018-4609-8b36-c31da3383932/access_as_user"],
                },
            },
            null
        ),
        RecaptchaModule,
        FormsModule,
        HttpClientModule,
        MaterialModule,
        MatNativeDateModule,
        ReactiveFormsModule,
        AppOverlayModule,
        LoaderModule,
        MaterialFileInputModule,
        NgxMaterialTimepickerModule.setOpts("en-GB", "latn"),
        LayoutModule,
        MatCarouselModule 
    ],
    //entryComponents: [
    //    AppComponent,
    //    LoaderComponent,
    //    //ProfileDialogComponent,
    //    //ProfileImageDialogComponent, 
    //    //IdentityDocumentImagesDialogComponent,
    //    //ChangePasswordDialogComponent,
    //    //AdmissionsApplicationDialogComponent,
    //    //UnSavedChangesDialogComponent,
    //    //HousingApplicationDialogComponent,
    //    //RoommatesDialogComponent,
    //    //ArrivalDialogComponent,
    //    //CheckoutDialogComponent,
    //    //AdmissionsApplicationDocumentsDialogComponent,
    //    //AdmissionsApplicationDocumentUploadDialogComponent,
    //    //RegisterDobConfirmationDialogComponent,
    //    //ConfirmationDialogComponent,
    //    //OrientationPreArrivalFormDialogComponent,
    //    //OrientationCheckListItemsDialogComponent,
    //    //OrientationCheckListItemDocumentUploadDialogComponent,
    //    //OrientationCheckListItemVideoDialogComponent,
    //    //PermitToStayCheckListItemsDialogComponent,
    //    //PermitToStayCheckListItemDocumentUploadDialogComponent,
    //    //PermitToStayAppointmentsDialogComponent,
    //    //IdentityDocumentHelpDialogComponent,
    //    //ProfileImageHelpDialogComponent
    //],
    providers: [
        { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
        { provide: LY_THEME, useClass: MinimaLight, multi: true },
        { provide: MAT_DATE_LOCALE, useValue: "en-US" },
        { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
        { provide: MAT_DATE_FORMATS, useValue: dateFormats },
        StorageService,
        AlertService,
        GoogleAnalyticsService,
        AppGlobals,
        MediaWatcherService 
    ],
    bootstrap: [AppComponent, MsalRedirectComponent],
    schemas: [
        CUSTOM_ELEMENTS_SCHEMA
    ],
}) as any)

export class AppModule {

}
